import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const STORAGE_KEY = 'markaii.auth';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStored(value) {
  if (!value) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function AuthProvider({ children }) {
  // Lazy init so the first render already knows whether we're authed.
  // This avoids a flash of "redirect to login" right before useEffect
  // hydrates from localStorage.
  const [state, setState] = useState(() => readStored());
  const [loading, setLoading] = useState(false);

  // Verify the stored token on mount by calling /auth/me. If it's
  // expired or revoked, clear it before any protected route renders.
  useEffect(() => {
    if (!state?.accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${state.accessToken}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          writeStored(null);
          setState(null);
        } else {
          const data = await res.json();
          const next = { ...state, user: data.user, organization: data.organization };
          writeStored(next);
          setState(next);
        }
      } catch {
        // Network blip — keep what we have; user can still try to use the app.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body.error || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      const next = {
        accessToken: body.accessToken,
        user: body.user,
        organization: body.organization,
      };
      writeStored(next);
      setState(next);
      return next;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new organization + owner user (ADR-0015 §3).
   * On success, the response shape mirrors /auth/login so the same
   * setState path applies — caller is signed in immediately.
   */
  const signup = useCallback(async ({ email, password, organization_name }) => {
    setLoading(true);
    try {
      const res = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, organization_name }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body.error || `HTTP ${res.status}`);
        err.status = res.status;
        err.body = body;
        throw err;
      }
      const next = {
        accessToken: body.accessToken,
        user: body.user,
        organization: body.organization,
      };
      writeStored(next);
      setState(next);
      return next;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Switch the active organization (ADR-0020). Requires the user to be a
   * member of `organization_id`. Server returns a fresh JWT scoped to the
   * new org; we swap it into localStorage so future api calls inherit
   * the new scope.
   *
   * Callers should invalidate any cached server data (typically with
   * `queryClient.clear()`) after this resolves — the previous data was
   * scoped to the OLD org and is now wrong.
   */
  const switchOrg = useCallback(async (organization_id) => {
    if (!state?.accessToken) throw new Error('not_authenticated');
    setLoading(true);
    try {
      const res = await fetch('/api/me/switch-org', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.accessToken}`,
        },
        body: JSON.stringify({ organization_id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body.error || `HTTP ${res.status}`);
        err.status = res.status;
        err.body = body;
        throw err;
      }
      const next = {
        ...state,
        accessToken: body.accessToken,
        organization: body.organization,
      };
      writeStored(next);
      setState(next);
      return next;
    } finally {
      setLoading(false);
    }
  }, [state]);

  const logout = useCallback(async () => {
    try {
      if (state?.accessToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${state.accessToken}` },
        });
      }
    } catch { /* ignore */ }
    writeStored(null);
    setState(null);
  }, [state]);

  const value = {
    user: state?.user || null,
    organization: state?.organization || null,
    accessToken: state?.accessToken || null,
    isAuthenticated: !!state?.accessToken,
    loading,
    login,
    signup,
    logout,
    switchOrg,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
